import { type ReactElement, useMemo } from 'react';

import { Switch } from '@/components/shadcn/ui/switch';
import { CHART_FIELD } from '@/entities/chart/model/constants';
import { useFeedbackTerminology } from '@/entities/workspace';
import { getChartFieldLabel } from '@/features/(charts)/chart-renderer/model/chart-maps';
import { TagInput, type TagValidationResult } from '@/shared/ui/tag-input';
import { useToast } from '@/stores/pure/ToastStore';

type Mode = 'keyword' | 'hashtag' | 'mention';
type DatasetChartFilterItemProps = ChartFilterItemProps & {
  showTopAction?: boolean;
};

const HASHTAG_VALIDATION_REGEX = /^(?!\d+$)[\p{L}\p{N}\p{M}_ ]+$/u;
const MENTION_VALIDATION_REGEX = /^(?!\d+$)[\p{L}\p{N}\p{M}_. ]+$/u;
const MODE_TO_FIELD: Record<Mode, CHART_FIELD> = {
  keyword: CHART_FIELD.FEEDBACK_KEYWORD,
  hashtag: CHART_FIELD.FEEDBACK_HASHTAG,
  mention: CHART_FIELD.FEEDBACK_MENTION,
};

const getPrefix = (mode: Exclude<Mode, 'keyword'>) => {
  return mode === 'hashtag' ? '#' : '@';
};

const stripPrefix = (value: string): string => {
  return value.replace(/^[@#]+/, '').trim();
};

const normalizeTextValue = (value: string, mode: Mode): string | null => {
  const cleaned = stripPrefix(value);
  if (!cleaned) return null;

  if (mode === 'keyword') {
    return cleaned;
  }

  const valid =
    mode === 'hashtag'
      ? HASHTAG_VALIDATION_REGEX.test(cleaned)
      : MENTION_VALIDATION_REGEX.test(cleaned);

  if (!valid) return null;
  return cleaned;
};

const toDisplayValue = (value: string, mode: Mode): string => {
  const normalized = normalizeTextValue(value, mode);
  if (!normalized) return value;

  if (mode === 'keyword') {
    return normalized;
  }

  return `${getPrefix(mode)}${normalized}`;
};

const createDatasetTagInput =
  (mode: Mode) =>
  ({
    values,
    onChange,
    disabled,
    showTopAction,
  }: DatasetChartFilterItemProps): ReactElement => {
    const addToast = useToast();
    const { replaceFeedbackTerminology } = useFeedbackTerminology();

    const displayValues = useMemo(
      () => values.map((value) => toDisplayValue(String(value), mode)),
      [mode, values],
    );
    const topLabel = replaceFeedbackTerminology(
      getChartFieldLabel(MODE_TO_FIELD[mode]),
    );

    const validateTag = (
      tag: string,
      currentTags: string[],
    ): TagValidationResult => {
      const normalized = normalizeTextValue(tag, mode);
      if (!normalized) return 'invalid';

      const hasDuplicate = currentTags
        .map((value) => normalizeTextValue(value, mode))
        .filter((value): value is string => !!value)
        .includes(normalized);

      if (hasDuplicate) return 'duplicate';
      return null;
    };

    const transformOnAdd = (tag: string) => {
      const normalized = normalizeTextValue(tag, mode);
      if (!normalized) return null;
      return toDisplayValue(normalized, mode);
    };

    const handleChange = (nextDisplayValues: string[]) => {
      const nextValues = nextDisplayValues
        .map((value) => normalizeTextValue(value, mode))
        .filter((value): value is string => !!value);

      onChange(nextValues);
    };

    const onValidationError = (result: TagValidationResult) => {
      if (result === 'duplicate') {
        addToast(`This ${mode} has already been added`, 'fail');
        return;
      }

      if (result !== 'invalid') return;

      if (mode === 'keyword') {
        addToast('Keyword cannot be empty', 'fail');
        return;
      }

      if (mode === 'hashtag') {
        addToast(
          'Hashtags can only contain letters, numbers, underscores. Numbers only are not allowed.',
          'fail',
        );
        return;
      }

      addToast(
        'Mentions can only contain letters, numbers, underscores, dots. Numbers only are not allowed.',
        'fail',
      );
    };

    return (
      <div className="w-full rounded-md border border-gray-400 bg-white p-2">
        {showTopAction && (
          <div className="mb-2 flex items-center justify-between gap-3 rounded-md bg-gray-100 px-3 py-2">
            <div className="text-xs font-medium text-gray-700">
              Top {topLabel}
            </div>
            <Switch
              checked={false}
              aria-label={`Toggle Top ${topLabel}`}
              disabled={disabled}
              onCheckedChange={(checked) => {
                if (checked) {
                  onChange([]);
                }
              }}
            />
          </div>
        )}
        <TagInput
          value={displayValues}
          onChange={handleChange}
          placeholder={`Enter ${mode}`}
          validateTag={validateTag}
          transformOnAdd={transformOnAdd}
          onValidationError={onValidationError}
          inputFilter={() => true}
          disabled={disabled}
        />
      </div>
    );
  };

export const KeywordDatasetTagInput = createDatasetTagInput('keyword');
export const HashtagDatasetTagInput = createDatasetTagInput('hashtag');
export const MentionDatasetTagInput = createDatasetTagInput('mention');

export const getDatasetTagInputByField = (
  field: CHART_FIELD,
): ((props: ChartFilterItemProps) => ReactElement | null) | undefined => {
  switch (field) {
    case CHART_FIELD.FEEDBACK_KEYWORD:
      return KeywordDatasetTagInput;
    case CHART_FIELD.FEEDBACK_HASHTAG:
      return HashtagDatasetTagInput;
    case CHART_FIELD.FEEDBACK_MENTION:
      return MentionDatasetTagInput;
    default:
      return undefined;
  }
};
